import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Avatar, AvatarFallback } from '@ophelos/ui/components/avatar';
import { getCreditorInitials } from '@/lib/creditor';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { Separator } from '@ophelos/ui/components/separator';
import { Link } from 'react-router-dom';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { migratedHistoryPath } from '@/lib/migratedHistoryLink';
import { displayFieldLabel, formatDisplayField } from '@/lib/purchase-date';
import type { DebtData } from '@/types';
import { ChargesBreakdown } from './ChargesBreakdown';
import { adjustmentTotals } from './charges';

interface DebtBreakdownTabProps {
  debt: DebtData | null;
}

/** Adjustments move the balance both ways, so the sign is explicit rather than implied by the type. */
function SignedAmount({ amount, currency }: { amount: number; currency?: string }) {
  if (amount === 0) return <CurrencyDisplay amount={0} currency={currency} />;
  return (
    <span className={amount < 0 ? 'text-green-600' : 'text-amber-600'}>
      {amount > 0 ? '+ ' : '- '}
      <CurrencyDisplay amount={Math.abs(amount)} currency={currency} />
    </span>
  );
}

export function DebtBreakdownTab({ debt }: DebtBreakdownTabProps) {
  const { t, currentLanguage } = useTranslation();

  if (!debt) {
    return null;
  }

  const adjustments = adjustmentTotals(debt.transactions ?? []);
  const hasAdjustments = adjustments.total !== 0;

  return (
    <div className="w-full h-full stack" data-testid="debt-breakdown-tab">
      {/* Header - Creditor Avatar and Name */}
      <div className="relative flex flex-col items-center gap-4 px-2 w-full border-b bg-linear-to-t from-accent border-ring py-8 -mt-6 overflow-hidden">
        <Avatar className="size-14">
          <AvatarFallback className="text-lg font-semibold bg-primary text-primary-foreground">
            {getCreditorInitials(debt.originalCreditor)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold truncate text-accent-foreground">{debt.originalCreditor}</h2>
        </div>
      </div>

      <div className="w-full sm:max-w-xl mx-auto px-4 flex-1 space-y-6 my-8 overflow-auto">
        {/* 1. Account Information */}
        <Card variant="fullscreen" className="gap-5 bg-soft border border-border-heavy shadow-none">
          <CardHeader>
            <CardTitle>{t('debtBreakdown.accountInformation')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('debtBreakdown.originalCreditor')}</span>
              <span className="text-sm font-medium">{debt.originalCreditor}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('debtBreakdown.accountNumber')}</span>
              <span className="text-sm font-medium">{debt.externalCaseRef ?? debt.accountNumber}</span>
            </div>
            {(debt.displayFields ?? []).map((field) => (
              <div key={field.id} className="flex items-center justify-between gap-4">
                <span className="text-sm text-muted-foreground shrink-0">{displayFieldLabel(field.labels, currentLanguage)}</span>
                <span className="text-sm font-medium text-right wrap-anywhere">{formatDisplayField(field, currentLanguage)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="mb-3 px-3">
          <span className="text-lg font-medium text-muted-foreground">{t('debtBreakdown.balanceTimeline')}</span>
        </div>

        {/* The pre-move history itself lives on the transactions tab; this only says it exists,
            since a migrated case opens at a residual and the balances below cannot explain it. */}
        {debt.migratedHistory?.events.length ? (
          <Alert variant="info">
            <AlertDescription className="flex flex-wrap items-center gap-x-2">
              {t('migrated.alert')}
              <Button asChild variant="link" size="sm" className="h-auto p-0">
                <Link to={migratedHistoryPath(debt.id)}>{t('migrated.alertLink')}</Link>
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {/* 2. Original Debt — what was owed at the start */}
        <Card variant="fullscreen" className="gap-5">
          <CardHeader>
            <CardTitle>{t('debtBreakdown.originalDebt')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className={`p-3 flex flex-col items-center rounded-xl bg-soft`}>
              <p className={`text-xs uppercase tracking-widest mb-1 text-muted-foreground`}>{t('debtBreakdown.originalDebt')}</p>
              <CurrencyDisplay
                amount={debt.originalBreakdown.total}
                currency={debt.currency}
                className={`text-xl font-bold tracking-tight text-muted-foreground`}
                centsClassName="text-xl"
              />
            </div>
            <div className="stack gap-2 px-2 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.principal')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.originalBreakdown.principal} currency={debt.currency} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.interest')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.originalBreakdown.interest} currency={debt.currency} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.fees')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.originalBreakdown.fees} currency={debt.currency} />
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Current Balance — what is owed now, the adjustments that got it there, and the
            charges behind it */}
        <Card variant="fullscreen" className="gap-5">
          <CardHeader>
            <CardTitle>{t('debtBreakdown.currentBalance')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Outstanding Balance hero */}
            <div className={`p-3 flex flex-col items-center rounded-xl ${debt.currentBalance === 0 ? 'bg-green-600/5' : 'bg-accent/50'}`}>
              <p className={`text-xs uppercase tracking-widest mb-1 ${debt.currentBalance === 0 ? 'text-green-600' : 'text-accent-foreground'}`}>
                {t('debtBreakdown.currentBalance')}
              </p>
              <CurrencyDisplay
                amount={debt.currentBalance}
                currency={debt.currency}
                className={`text-3xl font-bold tracking-tight ${debt.currentBalance === 0 ? 'text-green-600' : 'text-accent-foreground'}`}
                centsClassName="text-xl"
              />
            </div>
            <div className="stack gap-2 px-2 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.principal')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.outstandingBreakdown.principal} currency={debt.currency} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.interest')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.outstandingBreakdown.interest} currency={debt.currency} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('debtBreakdown.fees')}</span>
                <span className="text-sm font-medium">
                  <CurrencyDisplay amount={debt.outstandingBreakdown.fees} currency={debt.currency} />
                </span>
              </div>
            </div>

            {hasAdjustments && (
              <div className="mt-4 space-y-2">
                <Separator />
                <p className="text-sm font-semibold px-2 pt-2">{t('debtBreakdown.adjustments')}</p>
                <div className="stack gap-2 px-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('debtBreakdown.principalAdjustment')}</span>
                    <span className="text-sm font-medium">
                      <SignedAmount amount={adjustments.principal} currency={debt.currency} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('debtBreakdown.interestAdjustment')}</span>
                    <span className="text-sm font-medium">
                      <SignedAmount amount={adjustments.interest} currency={debt.currency} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('debtBreakdown.feesAdjustment')}</span>
                    <span className="text-sm font-medium">
                      <SignedAmount amount={adjustments.fees} currency={debt.currency} />
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{t('debtBreakdown.adjustmentsTotal')}</span>
                    <span className="text-sm font-bold">
                      <SignedAmount amount={adjustments.total} currency={debt.currency} />
                    </span>
                  </div>
                </div>
              </div>
            )}

            <ChargesBreakdown transactions={debt.transactions ?? []} currency={debt.currency} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
